import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, resetUserState } from '../redux/users/reducer';
import { agreementSections } from '../data/agreementSections';
import getOrdinalSuffix from '../utils/getOrdinalSuffix';
import { Container, Modal, Box, Button, Typography } from '@mui/material';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '../styles/Agreement.css';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  px: 3,
  py: 1,
  overflowY: 'scroll',
};

export default function Agreement() {
  const [modalIndex, setModalIndex] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const today = new Date();
  const day = today.getDate();
  const month = today.toLocaleString('default', { month: 'long' });
  const year = today.getFullYear();
  const formattedDate = `${day}${getOrdinalSuffix(day)} day of ${month}, ${year}`;

  const user = useSelector((state) => state.users.user);

  const isLoggedIn =
    useSelector((state) => state.users.isLoggedIn) &&
    localStorage.getItem('token') !== null;

  const handleAgree = () => {
    if (modalIndex < agreementSections.length - 1) {
      setModalIndex(modalIndex + 1);
    } else {
      agreeToTerms();
    }
  };

  const agreeToTerms = async () => {
    try {
      const updatedUser = {
        ...user,
        waiverSigned: true,
      };

      const response = await fetch(
        'https://api.innofitness.ca/users/waiver-signed',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            access: localStorage.getItem('token'),
          },
          body: JSON.stringify(updatedUser),
        }
      );

      if (response.ok) {
        dispatch(setUser(updatedUser));
        window.location.reload();
      }
    } catch (error) {
      if (error.message.includes('access token expired')) {
        localStorage.removeItem('token');
        dispatch(resetUserState());
        navigate('/');
      } else {
        window.alert('Error updating waiver status');
      }
    }
  };

  useEffect(() => {
    if (!user || !isLoggedIn) {
      navigate('/');
    }
  });

  if (!user.waiverSigned) {
    return (
      <Container
        className="page-container"
        sx={{ maxWidth: '1200px', px: 'max(2%, 16px) !important' }}
      >
        <Modal open={true}>
          <Box sx={modalStyle}>
            <Typography variant="h6" gutterBottom align="center">
              Page ({modalIndex + 1}/{agreementSections.length})
            </Typography>
            <Typography sx={{ mb: 1 }}>
              {agreementSections[modalIndex].content.map((entry, i) => {
                if (typeof entry === 'string')
                  return <Typography key={i}>{entry}</Typography>;

                let text = entry.text;
                if (entry.bold) {
                  entry.bold.forEach((phrase) => {
                    const parts = text.split(new RegExp(`(${phrase})`, 'gi'));
                    text = parts.map((part, j) =>
                      part.toLowerCase() === phrase.toLowerCase() ? (
                        <strong key={j}>{part}</strong>
                      ) : (
                        part
                      )
                    );
                  });
                }

                return (
                  <Typography
                    key={i}
                    sx={{
                      fontStyle: entry.italic ? 'italic' : 'normal',
                      mb: 1,
                      ml: entry.listItem ? '1rem' : 0,
                    }}
                  >
                    {text}
                  </Typography>
                );
              })}
              {modalIndex === agreementSections.length - 1 ? (
                <div
                  style={{
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '1rem',
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    align="center"
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    This Agreement was executed on {formattedDate}.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setModalIndex((prev) => prev - 1)}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleAgree}
                    >
                      Agree
                    </Button>
                  </Box>
                </div>
              ) : agreementSections[modalIndex].specialInitial ? (
                <div
                  style={{
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '1rem',
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    align="center"
                    sx={{
                      fontSize: '1rem',
                      color: 'red',
                    }}
                  >
                    I HAVE READ AND UNDERSTAND THIS AGREEMENT AND AGREE TO ABIDE
                    BY THE TERMS AND CONDITIONS OUTLINE HEREIN. I AM AWARE THAT
                    BY SIGNING THIS AGREEMENT I AM WAIVING CERTAIN LEGAL RIGHTS
                    WHICH I MAY HAVE AGAINST THE COMPANY.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setModalIndex((prev) => prev - 1)}
                    >
                      Prev
                    </Button>
                    <Button variant="contained" onClick={handleAgree}>
                      Yes
                    </Button>
                  </Box>
                </div>
              ) : (
                <div
                  style={{
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '1rem',
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    align="center"
                    sx={{
                      fontSize: '1rem',
                      color: 'red',
                    }}
                  >
                    I have read and fully understand this section.
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setModalIndex((prev) => prev - 1)}
                      disabled={modalIndex === 0}
                    >
                      Prev
                    </Button>
                    <Button variant="contained" onClick={handleAgree}>
                      Yes
                    </Button>
                  </Box>
                </div>
              )}
            </Typography>
          </Box>
        </Modal>
      </Container>
    );
  } else {
    return (
      <div class="agreement-pdf-file">
        <Typography
          variant="h6"
          gutterBottom
          align="center"
          sx={{
            fontSize: '1rem',
            fontWeight: 'bold',
            py: { sm: '20px' },
            textWrap: 'wrap',
          }}
        >
          You've already signed the agreement! Here's a copy in case you want it
          😊
        </Typography>
        <Worker
          workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
        >
          <Viewer fileUrl={'/Agreement.pdf'} />
        </Worker>
      </div>
    );
  }
}
